import React, { useEffect, useState, useRef, useMemo } from 'react'
import {
  isImageSlide,
  LightboxExternalProps,
  PluginProps,
  addToolbarButton,
} from 'yet-another-react-lightbox'
import { RotateButton } from './RotateButton'
import { Direction, RotatedImage, CrossOrigin } from './types'
import { applyDirection, rotateImage } from './utils'

export function useRotate({
  index,
  slides,
  crossOrigin,
}: Pick<LightboxExternalProps, 'slides' | 'index'> & {
  crossOrigin?: CrossOrigin
}) {
  const [rotating, setRotating] = useState<boolean>(false)
  const [rotatedImages, setRotatedImages] = useState<
    Map<React.Key, RotatedImage>
  >(() => new Map())
  const rotatedImagesRef = useRef(rotatedImages)

  useEffect(() => {
    rotatedImagesRef.current = rotatedImages
  })

  useEffect(
    () => () => {
      rotatedImagesRef.current.forEach(({ images }) =>
        Object.values(images).forEach(({ src }) => URL.revokeObjectURL(src)),
      )
      rotatedImagesRef.current.clear()
    },
    [],
  )

  const currentSlide =
    slides !== undefined &&
    index !== undefined &&
    index >= 0 &&
    index < slides.length &&
    isImageSlide(slides[index])
      ? slides[index]
      : undefined
  const currentSlideKey = currentSlide?.src

  const rotate = async () => {
    if (currentSlide === undefined || currentSlideKey === undefined || rotating)
      return

    setRotating(true)
    try {
      const rotatedImage: RotatedImage = rotatedImages.get(currentSlideKey) ?? {
        angle: 0,
        images: {
          0: await rotateImage(currentSlide.src, crossOrigin, 0),
        },
      }

      const angle = applyDirection(rotatedImage.angle, Direction.RIGHT)
      const newRotatedImage = { ...rotatedImage, angle }

      if (!newRotatedImage.images[angle]) {
        newRotatedImage.images[angle] = await rotateImage(
          newRotatedImage.images[0]?.src ?? '',
          crossOrigin,
          angle,
        )
      }

      const newRotatedImages = new Map(rotatedImages)
      newRotatedImages.set(currentSlideKey, newRotatedImage)
      setRotatedImages(newRotatedImages)
    } finally {
      setRotating(false)
    }
  }

  return {
    slides: useMemo(
      () =>
        slides?.map((slide) => {
          if (isImageSlide(slide)) {
            const rotatedImage = rotatedImages.get(slide.src)
            if (rotatedImage) {
              return {
                ...slide,
                srcSet: undefined,
                ...rotatedImage.images[rotatedImage.angle],
              }
            }
          }
          return slide
        }),
      [slides, rotatedImages],
    ),
    Rotate: ({ augment }: PluginProps) => {
      augment(({ toolbar, ...rest }) => ({
        toolbar: addToolbarButton(
          toolbar,
          'Rotate',
          <RotateButton
            disabled={!currentSlide}
            rotating={rotating}
            onClick={() => rotate()}
          />,
        ),
        ...rest,
      }))
    },
  }
}
